import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Map as MapIcon, FileSpreadsheet, Hash } from "lucide-react";
import { STATUS_META, ENTITY_META } from "@/lib/constants";
import { normalize } from "@/lib/utils";

const GOSTERILECEK = 8;

/**
 * Ctrl+K ile açılan hızlı atlama penceresi.
 *
 * 516 üyede aradığını bulmak için listeyi kaydırmak yerine iki harf yazıp
 * Enter'a basmak yeterli. Sıra numarasıyla da bulur: "#42" ya da "42".
 */
export default function QuickSearch({ members, onOpenDetail, onExport }) {
  const [acik, setAcik] = useState(false);
  const [sorgu, setSorgu] = useState("");
  const git = useNavigate();

  useEffect(() => {
    const dinle = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAcik((v) => !v);
      }
    };
    document.addEventListener("keydown", dinle);
    return () => document.removeEventListener("keydown", dinle);
  }, []);

  const sonuclar = useMemo(() => {
    const ham = sorgu.trim();
    if (!ham) return [];
    // Sıra numarası tam eşleşirse onu öne al
    const sayi = ham.match(/^#?\s*(\d{1,4})$/);
    if (sayi) {
      const n = parseInt(sayi[1], 10);
      const tam = members.filter((m) => m.order_no === n);
      if (tam.length) return tam.slice(0, GOSTERILECEK);
    }
    const q = normalize(ham);
    if (!q) return [];
    const terimler = q.split(" ").filter(Boolean);
    return members
      .filter((m) => {
        const saman = normalize([m.name, m.company, m.address, m.phone].filter(Boolean).join(" "));
        return terimler.every((t) => saman.includes(t));
      })
      .slice(0, GOSTERILECEK);
  }, [members, sorgu]);

  const kapatVe = (isle) => {
    setAcik(false);
    setSorgu("");
    isle();
  };

  return (
    <CommandDialog open={acik} onOpenChange={setAcik} commandProps={{ shouldFilter: false }}
      contentClassName="w-[calc(100%-2rem)] sm:w-full rounded-lg"
    >
      {/* cmdk kendi süzmesini yapmasın; sıralamayı yukarıdaki mantık belirliyor */}
      <CommandInput
        placeholder="Üye adı, şirket, sıra no veya telefon..."
        value={sorgu}
        onValueChange={setSorgu}
        data-testid="quick-search-input"
      />
      <CommandList>
        {sorgu.trim() && sonuclar.length === 0 && (
          <CommandEmpty>Eşleşen üye yok.</CommandEmpty>
        )}

        {sonuclar.length > 0 && (
          <CommandGroup heading="Üyeler">
            {sonuclar.map((m) => {
              const meta = STATUS_META[m.status] || STATUS_META.Beyaz;
              const em = ENTITY_META[m.entity_type] || ENTITY_META.Firma;
              return (
                <CommandItem
                  key={m.id}
                  value={`${m.id}`}
                  onSelect={() => kapatVe(() => onOpenDetail?.(m))}
                  data-testid={`quick-search-item-${m.id}`}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0 mr-2"
                    style={{ background: meta.dot }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">{m.name}</div>
                    {m.company && m.company !== m.name && (
                      <div className="text-[11px] text-neutral-500 truncate">{m.company}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-2">
                    <span className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded ${em.bg} ${em.text}`}>
                      {em.short}
                    </span>
                    {m.order_no != null && (
                      <span className="inline-flex items-center text-[10px] font-mono text-neutral-400">
                        <Hash className="h-2.5 w-2.5" />
                        {m.order_no}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        <CommandGroup heading="Git">
          <CommandItem value="rota" onSelect={() => kapatVe(() => git("/rota"))}>
            <MapIcon className="mr-2 h-4 w-4" />
            Saha rotası ve harita
          </CommandItem>
          {onExport && (
            <CommandItem value="excel" onSelect={() => kapatVe(onExport)}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel'e aktar
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
